import { useMemo, useState } from 'react';
import {
  briefEstVide,
  construireBriefDepuis,
  jourLocal,
  mesDabord,
  type BrouillonItem,
  type ChangementItem,
  type Item,
  type ActionIA,
  type PropositionIA,
  type MembrePublic,
} from '@camellie/shared';
import { LigneItem } from '../composants/LigneItem';
import { ActiverNotifs } from '../composants/ActiverNotifs';
import { BasculeVue } from '../composants/BasculeVue';
import { FicheItem } from '../composants/FicheItem';
import { Assistant } from '../composants/Assistant';
import { FUSEAU_FAMILLE, majuscule } from '../lib/format';

interface Props {
  items: Item[];
  membres: MembrePublic[];
  moi: MembrePublic;
  famille_id: string;
  vueTout: boolean;
  onVueTout: (v: boolean) => void;
  onBasculer: (id: string) => void;
  connecte: boolean;
  onDeconnecter: () => void;
  onAjouter: (brouillon: BrouillonItem) => void;
  /** Lot issu de l'assistant : cree et annulable au niveau de l'app. */
  onLotAssistant: (propositions: PropositionIA[]) => () => void;
  /** Gestes de l'assistant sur des elements existants, annulables de meme. */
  onAgirAssistant: (actions: ActionIA[]) => () => void;
  onModifier: (id: string, champs: ChangementItem) => void;
  onSupprimer: (id: string) => void;
}

/**
 * L'ecran d'accueil : le brief du jour.
 *
 * Il est recalcule localement a partir des items du magasin, avec exactement
 * la meme fonction que celle du Worker. Consequence directe : cocher une tache
 * la fait disparaitre du brief a l'instant, et le brief change aussi quand
 * l'autre telephone coche quelque chose, sans rafraichir.
 */
export function EcranBrief({
  items,
  membres,
  moi,
  famille_id,
  vueTout,
  onVueTout,
  onBasculer,
  connecte,
  onDeconnecter,
  onAjouter,
  onLotAssistant,
  onAgirAssistant,
  onModifier,
  onSupprimer,
}: Props) {
  const [enEdition, setEnEdition] = useState<string | null>(null);
  const [creation, setCreation] = useState(false);
  const [assistant, setAssistant] = useState(false);
  const [montrerFaites, setMontrerFaites] = useState(false);

  // Le brief est calcule sur les items visibles : par defaut ce qui m'est
  // assigne, plus ce qui n'est assigne a personne. Deux telephones affichent
  // donc deux briefs differents a partir des memes donnees.
  const visibles = useMemo(
    () => (vueTout ? items : items.filter((i) => i.assigne_a === moi.id || i.assigne_a === null)),
    [items, moi.id, vueTout],
  );
  const caches = useMemo(
    () =>
      items.filter((i) => i.assigne_a !== null && i.assigne_a !== moi.id && i.statut === 'todo')
        .length,
    [items, moi.id],
  );

  const brief = useMemo(
    () => construireBriefDepuis(visibles, famille_id, FUSEAU_FAMILLE),
    [visibles, famille_id],
  );

  const parId = useMemo(() => new Map(membres.map((m) => [m.id, m])), [membres]);
  /**
   * Deux listes que le brief ne couvre pas, mais qui n'ont plus d'autre ecran
   * depuis la disparition de l'onglet Taches : ce qui n'a pas d'echeance, et
   * ce qui vient d'etre coche — sans quoi decocher une erreur serait
   * impossible.
   */
  const sansDate = useMemo(
    () => visibles.filter((i) => i.type === 'tache' && i.statut === 'todo' && !i.du_le),
    [visibles],
  );
  const faitesAujourdhui = useMemo(
    () =>
      visibles
        .filter((i) => i.statut === 'fait' && i.complete_le && jourLocal(new Date(i.complete_le), FUSEAU_FAMILLE) === brief.date_locale)
        .sort((a, b) => (b.complete_le ?? '').localeCompare(a.complete_le ?? '')),
    [visibles, brief.date_locale],
  );

  const vide = briefEstVide(brief) && sansDate.length === 0 && faitesAujourdhui.length === 0;
  const item = enEdition ? (items.find((i) => i.id === enEdition) ?? null) : null;

  return (
    <>
      <div className="entete">
        <h1>{majuscule(brief.libelle_date)}</h1>
        {/* La barre du bas est desormais occupee par quatre onglets : le
            profil et l'etat de connexion remontent ici. */}
        <button type="button" className="profil" onClick={onDeconnecter}>
          <span className={'pastille' + (connecte ? ' en-ligne' : '')} aria-hidden="true" />
          {moi.prenom}
        </button>
      </div>

      <div className="contenu">
        <BasculeVue vueTout={vueTout} onChange={onVueTout} caches={caches} />

        <div className="duo-ajout">
          <button type="button" className="ajouter-gros" onClick={() => setCreation(true)}>
            <span aria-hidden="true">+</span> Ajouter une tache
          </button>
          <button type="button" className="bouton-assistant" onClick={() => setAssistant(true)}>
            <span aria-hidden="true">✨</span> Assistant
          </button>
        </div>

        {vide && (
          <div className="vide">
            <strong>Rien de prevu aujourd hui.</strong>
            {vueTout ? 'Profitez-en.' : 'Rien ne t est assigne pour aujourd hui.'}
          </div>
        )}

        <Section
          titre="Aujourd hui"
          items={mesDabord(brief.rdv_du_jour, moi.id)}
          membres={parId}
          moi={moi.id}
          onOuvrir={setEnEdition}
        />

        <Section
          titre="A faire aujourd hui"
          items={mesDabord(brief.taches_du_jour, moi.id)}
          membres={parId}
          moi={moi.id}
          onBasculer={onBasculer}
          onOuvrir={setEnEdition}
        />

        <Section
          titre="En retard"
          classe="retard"
          items={mesDabord(brief.taches_en_retard, moi.id)}
          membres={parId}
          moi={moi.id}
          onBasculer={onBasculer}
          onOuvrir={setEnEdition}
          montrerDate
        />

        <Section
          titre="Sans date"
          items={mesDabord(sansDate, moi.id)}
          membres={parId}
          moi={moi.id}
          onBasculer={onBasculer}
          onOuvrir={setEnEdition}
        />

        {faitesAujourdhui.length > 0 && (
          <section className="section">
            <h2>
              <button
                type="button"
                className="bouton discret"
                style={{ padding: 0, minHeight: 'auto', font: 'inherit', letterSpacing: 'inherit' }}
                onClick={() => setMontrerFaites((v) => !v)}
                aria-expanded={montrerFaites}
              >
                {montrerFaites ? 'Masquer' : 'Voir'} les {faitesAujourdhui.length} faites aujourd hui
              </button>
            </h2>
            {montrerFaites && (
              <div className="liste">
                {faitesAujourdhui.map((i) => (
                  <LigneItem
                    key={i.id}
                    item={i}
                    membres={parId}
                    moi={moi.id}
                    onBasculer={onBasculer}
                    onOuvrir={setEnEdition}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        <ActiverNotifs />
      </div>

      {(item || creation) && (
        <FicheItem
          item={item}
          typeNouveau="tache"
          membres={membres}
          moi={moi.id}
          onEnregistrer={onModifier}
          onCreer={onAjouter}
          onSupprimer={onSupprimer}
          onFermer={() => {
            setEnEdition(null);
            setCreation(false);
          }}
        />
      )}
      {assistant && (
        <Assistant
          onAjouter={onLotAssistant}
          onAgir={onAgirAssistant}
          onFermer={() => setAssistant(false)}
        />
      )}

   </>
  );
}

interface PropsSection {
  titre: string;
  items: Item[];
  membres: Map<string, MembrePublic>;
  moi: string;
  classe?: string;
  onBasculer?: (id: string) => void;
  onOuvrir?: (id: string) => void;
  montrerDate?: boolean;
}

function Section({ titre, items, membres, moi, classe, onBasculer, onOuvrir, montrerDate }: PropsSection) {
  if (items.length === 0) return null;
  return (
    <section className={'section ' + (classe ?? '')}>
      <h2>{titre}</h2>
      <div className="liste">
        {items.map((item) => (
          <LigneItem
            key={item.id}
            item={item}
            membres={membres}
            moi={moi}
            onBasculer={onBasculer}
            onOuvrir={onOuvrir}
            montrerDate={montrerDate}
          />
        ))}
      </div>
    </section>
  );
}
